/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useRef, useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import screenfull from 'screenfull';
import PlayerControls from 'Pages/private/video-player-0/PLayerControls';
import HlsPlayer from 'Pages/private/video-player-0/HlsPlayer';
import NativeVideoPlayer from 'Pages/private/video-player-0/NativeVideoPlayer';
import { probeVideo } from 'Pages/private/video-player-0/lib/probeVideo';
import { classifyFormat } from 'Pages/private/video-player-0/lib/formats';
import styles from 'Pages/private/video-player-0/index.module.css';

const useStyles = makeStyles({
  playerWrapper: {
    width: '100%',
    position: 'relative',
    height: '100%',
    background: 'black',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    '& video': { objectFit: 'contain' },
    '&:fullscreen': { height: '100%', width: '100%' },
    '&:-webkit-full-screen': { height: '100%', width: '100%' },
  },
});

const format = (seconds) => {
  if (Number.isNaN(seconds)) return '00:00';
  const date = new Date(seconds * 1000);
  const hh = date.getUTCHours();
  const mm = date.getUTCMinutes();
  const ss = date.getUTCSeconds().toString().padStart(2, '0');
  if (hh) return `${hh}:${mm.toString().padStart(2, '0')}:${ss}`;
  return `${mm}:${ss}`;
};

// Self-contained public video player. Reuses the app's HLS/native playback
// engine + PlayerControls UI, but is fed DIRECT urls from the share API
// (no token fetch, view-tracking, or next-video autoplay).
const SharedVideoPlayer = ({ item, onBack }) => {
  const classes = useStyles();
  const primaryUrl = item?.transcoded_url || item?.video_file || item?.mp4_url;

  const [currentVideoUrl, setCurrentVideoUrl] = useState(primaryUrl || null);
  const [hasTriedFallback, setHasTriedFallback] = useState(false);
  const [probe, setProbe] = useState(null);
  const [hasStartedPlayback, setHasStartedPlayback] = useState(false);
  const [loadPercent, setLoadPercent] = useState(0);
  const [screenSize, setScreenSize] = useState(false);
  const [showControllers, setControllers] = useState(true);
  const [hoverValue, setHoverValue] = useState(null);
  const [showPlayAgainButton, setPlayAgainButton] = useState(false);

  const [state, setState] = useState({
    playing: true,
    muted: false,
    volume: parseFloat(localStorage.getItem('volume')) || 0.5,
    playbackRate: 1.0,
    played: 0,
    seeking: false,
    buffering: true,
  });
  const { playing, muted, volume, playbackRate, played, seeking, buffering, loaded = 0 } = state;

  const playerRef = useRef(null);
  const playerContainerRef = useRef(null);
  const controlsRef = useRef(null);
  const timeoutRef = useRef(null);
  const bufferingRef = useRef(buffering);
  const stalledAtPlayedSecondsRef = useRef(null);
  const finalizingRef = useRef(false);
  const loadTimeoutRef = useRef(null);

  useEffect(() => { bufferingRef.current = buffering; }, [buffering]);
  useEffect(() => { localStorage.setItem('volume', volume); }, [volume]);

  const fallbackUrl = item?.mp4_url && item.mp4_url !== currentVideoUrl
    ? item.mp4_url
    : (item?.video_file && item.video_file !== currentVideoUrl ? item.video_file : null);

  // Probe non-HLS urls so NativeVideoPlayer can pick its preload strategy.
  useEffect(() => {
    if (!currentVideoUrl || currentVideoUrl.includes('.m3u8')) {
      setProbe(null);
      return undefined;
    }
    const ctrl = new AbortController();
    (async () => {
      try {
        const p = await probeVideo(currentVideoUrl, ctrl.signal);
        if (!ctrl.signal.aborted) setProbe(p);
      } catch (e) { /* best-effort */ }
    })();
    return () => ctrl.abort();
  }, [currentVideoUrl]);

  // Loading timeout → try fallback url once.
  useEffect(() => {
    if (!currentVideoUrl || hasTriedFallback) return undefined;
    clearTimeout(loadTimeoutRef.current);
    const timeoutDuration = currentVideoUrl.includes('.m3u8') ? 10000 : 6000;
    loadTimeoutRef.current = setTimeout(() => {
      if (!hasTriedFallback && fallbackUrl) {
        setCurrentVideoUrl(fallbackUrl);
        setHasTriedFallback(true);
      }
    }, timeoutDuration);
    return () => clearTimeout(loadTimeoutRef.current);
  }, [currentVideoUrl, hasTriedFallback, fallbackUrl]);

  const handleBufferStart = () => {
    if (bufferingRef.current) return;
    const current = playerRef.current?.getCurrentTime?.();
    stalledAtPlayedSecondsRef.current = typeof current === 'number' ? current : null;
    finalizingRef.current = false;
    setState((prev) => ({ ...prev, buffering: true }));
  };

  const handleVideoError = (error) => {
    if (error?.name === 'NotAllowedError' || (error?.message && error.message.includes('interact'))) {
      setState((prev) => ({ ...prev, playing: false, buffering: false }));
      return;
    }
    clearTimeout(loadTimeoutRef.current);
    if (!hasTriedFallback && fallbackUrl) {
      setCurrentVideoUrl(fallbackUrl);
      setHasTriedFallback(true);
    }
  };

  const handleVideoReady = () => setLoadPercent((prev) => Math.max(prev, 50));

  const handleVideoPlay = () => {
    clearTimeout(loadTimeoutRef.current);
    loadTimeoutRef.current = null;
    setHasStartedPlayback(true);
  };

  const onEndBuffering = () => {
    clearTimeout(loadTimeoutRef.current);
    loadTimeoutRef.current = null;
    setLoadPercent((prev) => Math.max(prev, 85));
  };

  const onStarted = () => {};

  const handleVideoEnded = () => setPlayAgainButton(true);

  const handlePlayPause = () => setState((prev) => ({ ...prev, playing: !prev.playing }));
  const handleRewind = () => playerRef.current.seekTo(playerRef.current.getCurrentTime() - 15);
  const handleFastForward = () => playerRef.current.seekTo(playerRef.current.getCurrentTime() + 15);
  const handleMute = () => setState((prev) => ({ ...prev, muted: !prev.muted }));
  const handleVolumeChange = (newValue) => setState((prev) => ({
    ...prev, volume: Math.floor(newValue), muted: newValue === 0,
  }));
  const handleVolumeSeekDown = (newValue) => setState((prev) => (
    newValue < 0.027
      ? { ...prev, volume: 0, muted: true }
      : { ...prev, volume: newValue, muted: false }
  ));
  const handelPlaybackRate = (value) => setState((prev) => ({ ...prev, playbackRate: value }));
  const toggleFullScreen = () => {
    screenfull.toggle(playerContainerRef.current);
    setScreenSize(!screenSize);
  };

  const handleProgress = (changeState) => {
    if (seeking) return;
    const stalledAt = stalledAtPlayedSecondsRef.current;
    const dur = playerRef.current?.getDuration?.() || 0;
    const resumeTarget = dur > 0 ? Math.min(stalledAt + 0.5, dur - 0.25) : stalledAt + 0.5;
    const hasAdvanced = stalledAt === null
      ? changeState.playedSeconds > 0
      : changeState.playedSeconds > resumeTarget;
    if (hasAdvanced && buffering && !finalizingRef.current) {
      finalizingRef.current = true;
      setLoadPercent(100);
      setTimeout(() => {
        setState((prev) => ({ ...prev, buffering: false }));
        stalledAtPlayedSecondsRef.current = null;
        finalizingRef.current = false;
      }, 400);
    }
    setState((prev) => ({ ...prev, ...changeState }));
  };

  const handleSeekChange = (newValue) => setState((prev) => ({ ...prev, played: newValue }));
  const handleSeekMouseDown = () => setState((prev) => ({ ...prev, seeking: true }));
  const handleSeekMouseUp = (newValue) => {
    setState((prev) => ({ ...prev, seeking: false }));
    playerRef.current.seekTo(newValue);
  };

  const currentTime = playerRef.current ? playerRef.current.getCurrentTime() : '00:00';
  const duration = playerRef.current ? playerRef.current.getDuration() : '00:00';
  const elapsedTime = format(currentTime);
  const totalDuration = format(duration);

  // Keyboard shortcuts (space / arrows).
  useEffect(() => {
    const onKey = (event) => {
      if (event.keyCode === 32) handlePlayPause();
      if (event.keyCode === 37) handleRewind();
      if (event.keyCode === 39) handleFastForward();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // Auto show/hide the controls overlay on idle.
  const showOrHideControllers = () => {
    if (controlsRef.current && controlsRef.current.style.visibility !== 'visible') {
      controlsRef.current.style.visibility = 'visible';
    }
    if (showPlayAgainButton && !buffering) {
      clearTimeout(timeoutRef.current);
    } else {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        if (controlsRef.current && controlsRef.current.style.visibility !== 'hidden') {
          controlsRef.current.style.visibility = 'hidden';
          setHoverValue(null);
        }
      }, 2000);
    }
  };

  useEffect(() => {
    if (buffering && controlsRef.current) controlsRef.current.style.visibility = 'visible';
  }, [buffering]);

  useEffect(() => {
    if (!showPlayAgainButton && !buffering) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        if (controlsRef.current && controlsRef.current.style.visibility !== 'hidden') {
          controlsRef.current.style.visibility = 'hidden';
          setHoverValue(null);
        }
      }, 2000);
      ['mousemove', 'click', 'touchstart'].forEach((evt) => document.addEventListener(evt, showOrHideControllers, false));
      return () => {
        clearTimeout(timeoutRef.current);
        ['mousemove', 'click', 'touchstart'].forEach((evt) => document.removeEventListener(evt, showOrHideControllers, false));
      };
    }
    return undefined;
  }, [showPlayAgainButton, playing, buffering]);

  // Replay from the start when the completed overlay is dismissed.
  useEffect(() => {
    if (showPlayAgainButton) return;
    const player = playerRef.current;
    if (!player) return;
    const cur = player.getCurrentTime?.() || 0;
    const dur = player.getDuration?.() || 0;
    if (dur > 0 && cur >= dur - 0.5) {
      player.seekTo(0);
      const internal = player.getInternalPlayer?.();
      if (internal) {
        try { internal.currentTime = 0; } catch (e) { /* ignore */ }
        const p = internal.play?.();
        if (p && p.catch) p.catch(() => {});
      }
      setState((prev) => ({ ...prev, playing: true, played: 0 }));
    }
  }, [showPlayAgainButton]);

  const isHls = currentVideoUrl?.includes('.m3u8');

  return (
    <div ref={playerContainerRef} className={classes.playerWrapper}>
      <div className={styles.videoContainer}>
        {isHls ? (
          <HlsPlayer
            key={currentVideoUrl}
            ref={playerRef}
            width="100%"
            height="100%"
            className={styles.react_player}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            url={currentVideoUrl}
            muted={muted}
            playing={playing}
            volume={volume}
            playbackRate={playbackRate}
            onProgress={handleProgress}
            onBuffer={handleBufferStart}
            onBufferEnd={onEndBuffering}
            onReady={handleVideoReady}
            onStart={onStarted}
            onError={handleVideoError}
            onPlay={handleVideoPlay}
            onEnded={handleVideoEnded}
          />
        ) : (
          <>
            <NativeVideoPlayer
              key={currentVideoUrl}
              ref={playerRef}
              width="100%"
              height="100%"
              className={styles.react_player}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              url={currentVideoUrl}
              probe={probe}
              muted={muted}
              playing={playing}
              volume={volume}
              playbackRate={playbackRate}
              onProgress={handleProgress}
              onBuffer={handleBufferStart}
              onBufferEnd={onEndBuffering}
              onReady={handleVideoReady}
              onStart={onStarted}
              autoPlay
              onPlay={handleVideoPlay}
              onEnded={handleVideoEnded}
              progressInterval={100}
            />
            {(() => {
              if (!currentVideoUrl || hasStartedPlayback) return null;
              const cls = classifyFormat(currentVideoUrl, probe);
              if (cls.kind !== 'mp4' && cls.kind !== 'webm') return null;
              return (
                <video
                  key={`warmup-${currentVideoUrl}`}
                  src={currentVideoUrl}
                  preload="auto"
                  muted
                  playsInline
                  aria-hidden="true"
                  tabIndex={-1}
                  style={{
                    position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none',
                  }}
                />
              );
            })()}
          </>
        )}
      </div>
      <div>
        <PlayerControls
          ref={controlsRef}
          onPlayPause={handlePlayPause}
          playing={playing}
          onRewind={handleRewind}
          onFastForward={handleFastForward}
          muted={muted}
          onMute={handleMute}
          volume={volume}
          onVolumeChange={handleVolumeChange}
          onVolumeSeekUp={handleVolumeSeekDown}
          playbackRate={playbackRate}
          onPlaybackRateChange={handelPlaybackRate}
          onToggleFullScreen={toggleFullScreen}
          played={played}
          onSeek={handleSeekChange}
          onSeekMouseDown={handleSeekMouseDown}
          onSeekMouseUp={handleSeekMouseUp}
          currentTime={currentTime}
          elapsedTime={elapsedTime}
          totalDuration={totalDuration}
          title={item?.title}
          videoID={item?.id}
          buffering={buffering}
          loadPercent={loadPercent}
          loaded={loaded}
          vttContent={undefined}
          duration={duration}
          rows={undefined}
          columns={undefined}
          spriteImage={undefined}
          screenSize={screenSize}
          format={format}
          showPlayAgainButton={showPlayAgainButton}
          setPlayAgainButton={setPlayAgainButton}
          showControllers={showControllers}
          setControllers={setControllers}
          hoverValue={hoverValue}
          setHoverValue={setHoverValue}
          setState={setState}
          state={state}
          nextVideoDetails={undefined}
          countDown={0}
          from={undefined}
          onBack={onBack}
        />
      </div>
    </div>
  );
};

export default SharedVideoPlayer;
